import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';

export default function SubscriptionStatus() {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewing, setRenewing] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const data = await apiService.getSubscriptionStatus();
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRenew = async () => {
    try {
      setRenewing(true);
      const response = await apiService.renewSubscription();
      
      if (response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      }
    } catch (error) {
      console.error('Error renewing subscription:', error);
      alert('فشل تجديد الاشتراك. حاول مرة أخرى.');
      setRenewing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 animate-pulse">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
      </div>
    );
  }

  if (!subscription || !subscription.hasSubscription) {
    return null;
  }

  const { status, urgency, endDate, daysRemaining } = subscription;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-DZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = () => {
    switch (urgency) {
      case 'expired':
        return (
          <div className="bg-red-100 border border-red-400 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">❌</span>
                  <h3 className="text-xl font-bold text-red-800">انتهى اشتراكك</h3>
                </div>
                <p className="text-red-700 mb-2">آخر تجديد: {formatDate(endDate)}</p>
                <p className="text-red-600 text-sm">
                  جدد اشتراكك الآن للاستمرار في الوصول لجميع الدورات والقصص التعليمية
                </p>
              </div>
              <button
                onClick={handleRenew}
                disabled={renewing}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {renewing ? 'جاري التحويل...' : 'جدد اشتراكك الآن'}
              </button>
            </div>
          </div>
        );

      case 'critical':
        return (
          <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl animate-pulse">⚠️</span>
                  <h3 className="text-xl font-bold text-red-800">آخر فرصة: اشتراكك ينتهي {daysRemaining === 1 ? 'غداً' : 'خلال يوم واحد'}</h3>
                </div>
                <p className="text-red-700 mb-2">ينتهي في: {formatDate(endDate)}</p>
                <p className="text-red-600 text-sm font-medium">
                  جدد الآن لتستمر بالوصول للمحتوى بدون انقطاع!
                </p>
              </div>
              <button
                onClick={handleRenew}
                disabled={renewing}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-lg"
              >
                {renewing ? 'جاري التحويل...' : 'جدد دابا'}
              </button>
            </div>
          </div>
        );

      case 'urgent':
        return (
          <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">🚨</span>
                  <h3 className="text-xl font-bold text-orange-800">اشتراكك ينتهي خلال {daysRemaining} أيام</h3>
                </div>
                <p className="text-orange-700 mb-2">ينتهي في: {formatDate(endDate)}</p>
                <p className="text-orange-600 text-sm">
                  جدد اشتراكك الآن لتستمر في التعلم بدون انقطاع
                </p>
              </div>
              <button
                onClick={handleRenew}
                disabled={renewing}
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {renewing ? 'جاري التحويل...' : 'جدد اشتراكك'}
              </button>
            </div>
          </div>
        );

      case 'warning':
        return (
          <div className="bg-yellow-50 border border-yellow-400 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">⚠️</span>
                  <h3 className="text-lg font-bold text-yellow-800">اشتراكك ينتهي قريباً</h3>
                </div>
                <p className="text-yellow-700 mb-1">ينتهي في: {formatDate(endDate)} ({daysRemaining} أيام متبقية)</p>
                <p className="text-yellow-600 text-sm">
                  نذكرك بتجديد اشتراكك قبل انتهائه
                </p>
              </div>
              <button
                onClick={handleRenew}
                disabled={renewing}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {renewing ? 'جاري التحويل...' : 'جدد الآن'}
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-green-50 border border-green-400 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <span className="text-2xl">✓</span>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-green-800">اشتراك شهري نشط</h3>
                <p className="text-green-700 text-sm">
                  ينتهي في: {formatDate(endDate)} ({daysRemaining} يوماً متبقية)
                </p>
              </div>
            </div>
          </div>
        );
    }
  };

  // Compact badge for header (next to name)
  const getCompactBadge = () => {
    if (!subscription || !subscription.hasSubscription) return null;
    
    if (urgency === 'expired') {
      return (
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 border-2 border-red-400 text-red-800 rounded-xl text-sm font-bold">
          <i className="fas fa-times-circle"></i>
          <span>اشتراك منتهي</span>
        </div>
      );
    }
    
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 border-2 border-green-400 text-green-800 rounded-xl text-sm font-bold">
        <i className="fas fa-check-circle"></i>
        <div className="flex flex-col items-start">
          <span>اشتراك شهري نشط</span>
          <span className="text-xs font-normal">ينتهي في: {formatDate(endDate)} ({daysRemaining} يوماً متبقية)</span>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-6">
      {getStatusBadge()}
    </div>
  );
}

// Export the compact badge for use in header
export function SubscriptionCompactBadge() {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        setLoading(true);
        const data = await apiService.getSubscriptionStatus();
        setSubscription(data);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubscriptionStatus();
  }, []);

  if (loading || !subscription || !subscription.hasSubscription) return null;

  const { urgency, endDate, daysRemaining } = subscription;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-DZ', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (urgency === 'expired') {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 border-2 border-red-400 text-red-800 rounded-xl text-sm font-bold">
        <i className="fas fa-times-circle"></i>
        <span>اشتراك منتهي</span>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 border-2 border-green-400 text-green-800 rounded-xl text-sm font-bold shadow-md">
      <i className="fas fa-check-circle"></i>
      <div className="flex flex-col items-start">
        <span>✓ اشتراك شهري نشط</span>
        <span className="text-xs font-normal text-green-700">ينتهي في: {formatDate(endDate)} ({daysRemaining} يوماً متبقية)</span>
      </div>
    </div>
  );
}
